import * as d3 from "d3";

export class ProctorGraph {
	async proctoringGraph(
		selector,
		headMovementData,
		mouthMovementData,
		cheatingProbabilityData,
		eyeTrackingData,
		cheatingWindowSizeInSec,
		time = null,
		agentProctoringTimes = [],
		browserProctoringTimes = [],
		windowData = {},
		onTimeClick = null
	) {
		console.log("the time is", time);
		// if(time) time = 1813;
		// Set up container
		const container = d3.select(selector);
		container.style("position", "relative");
		console.log("the container is", container);

		// Calculate dimensions
		const containerWidth = container.node().clientWidth;
		const containerHeight = container.node().clientHeight;
		const margin = { top: 20, right: 30, bottom: 50, left: 50 }; // Increased bottom margin for markers

		const categories = [
			{ key: "Head Movement", data: headMovementData },
			{ key: "Mouth Movement", data: mouthMovementData },
			{ key: "Cheating Probability", data: cheatingProbabilityData },
			{ key: "Eye Tracking", data: eyeTrackingData },
		];

		// Filter out categories with no data
		const validCategories = categories.filter(
			(cat) => Array.isArray(cat.data) && cat.data.length > 0
		);

		const maxLength = d3.max(validCategories, (c) => c.data.length);
		console.log("Calculated maxLength:", maxLength);

		// Calculate SVG dimensions based on zoom level
		let zoomLevel = 1;
		const baseWidth = containerWidth - margin.left - margin.right;
		const height = containerHeight - margin.top - margin.bottom;
		let currentSvgWidth = baseWidth;

		// Create SVG with clipping and scrolling capability
		// const svg = container
		// 	.append("svg")
		// 	.attr("width", containerWidth)
		// 	.attr("height", containerHeight)
		// 	.style("overflow", "auto");\

		const svg = container
			.append("svg")
			.attr("viewBox", `0 0 ${baseWidth + 60} ${containerHeight}`)
			.attr("preserveAspectRatio", "none")
			.style("width", "100%")
			.style("height", "100%");

		console.log("the svg is", svg);
		// Create a clipping path for the chart area
		const defs = svg.append("defs");
		defs
			.append("clipPath")
			.attr("id", "chart-clip")
			.append("rect")
			.attr("x", 0)
			.attr("y", 0)
			.attr("width", baseWidth)
			.attr("height", height + 40);

		// Create the main group that will be translated for scrolling
		const mainGroup = svg
			.append("g")
			.attr("transform", `translate(${margin.left},${margin.top})`);
		console.log("the main group is", mainGroup);

		// Create the chart group that will contain the scrollable content
		const chartGroup = mainGroup
			.append("g")
			.attr("clip-path", "url(#chart-clip)");

		// Create the content group that will be translated for scrolling
		const contentGroup = chartGroup.append("g");

		// Track scroll position
		let scrollX = 0;

		const series = validCategories.map((cat) => ({
			key: cat.key,
			values: cat.data.map((val, i) => ({
				time: i + 1,
				value: val,
			})),
		}));

		// Create scales
		let x = d3
			.scaleLinear()
			.domain([1, maxLength])
			.range([0, currentSvgWidth]);

		const y = d3
			.scaleLinear()
			.domain([0, d3.max(series, (s) => d3.max(s.values, (d) => d.value))])
			.nice()
			.range([height, 0]);

		const color = d3
			.scaleOrdinal()
			.domain(categories.map((c) => c.key))
			.range(["#AD1927", "#038E00", "#FFC145", "#20639B"]);

		// Function to calculate window time ranges
		function calculateWindowRanges(windowData) {
			const ranges = [];
			Object.entries(windowData).forEach(([key, data]) => {
				const windowNumber = data.window;
				const duration = data.duration;

				// Calculate start time (window * 35 seconds)
				const startTime = windowNumber * cheatingWindowSizeInSec;
				const endTime = startTime + duration;

				ranges.push({
					key: key,
					start: startTime,
					end: endTime,
					window: windowNumber,
					duration: duration,
				});
			});
			return ranges;
		}

		// Calculate window ranges
		const windowRanges = calculateWindowRanges(windowData);

		// Create window highlight areas (add before lines so they appear behind)
		const windowHighlights = contentGroup
			.selectAll(".window-highlight")
			.data(windowRanges)
			.enter()
			.append("rect")
			.attr("class", "window-highlight")
			.attr("x", (d) => x(d.start))
			.attr("y", 0)
			.attr("width", (d) => x(d.end) - x(d.start))	
			.attr("height", height)
			.attr("fill", "red")
			.attr("opacity", 0.2)
			.attr("pointer-events", "none"); // Don't interfere with mouse events

		// Create line generator
		const lineGenerator = d3
			.line()
			.x((d) => x(d.time))
			.y((d) => y(d.value))
			.curve(d3.curveMonotoneX);

		// Draw lines in the content group
		const lines = contentGroup
			.selectAll(".line")
			.data(series)
			.enter()
			.append("path")
			.attr("class", "line")
			.attr("fill", "none")
			.attr("stroke", (d) => color(d.key))
			.attr("stroke-width", 1.5)
			.attr("d", (d) => lineGenerator(d.values));

		// X-axis ticks interval logic
		let tickInterval;
		if (maxLength <= 60) tickInterval = 10;
		else if (maxLength <= 600) tickInterval = 30;
		else tickInterval = 60;

		// Create axes in the main group (outside clipping)
		const xAxisGroup = mainGroup
			.append("g")
			.attr("transform", `translate(0,${height + 10})`)
			.attr("class", "axis-x");

		mainGroup.append("g").call(d3.axisLeft(y));

		// Create grids in the content group (inside clipping)
		const yGridGroup = contentGroup.append("g").attr("class", "grid y-grid");

		const xGridGroup = contentGroup
			.append("g")
			.attr("class", "grid x-grid")
			.attr("transform", `translate(0,${height})`);

		let agentMarkers, browserMarkers;
		let proctoringBaseLine;
		// Create container for markers (keep existing transform)
		const proctoringMarkersGroup = contentGroup
			.append("g")
			.attr("class", "proctoring-markers")
			.attr("transform", `translate(0,${height + 25})`);

		proctoringBaseLine = proctoringMarkersGroup
			.append("line")
			.attr("class", "proctoring-base-line")
			.attr("x1", 0)
			.attr("x2", currentSvgWidth)
			.attr("y1", -15) // Position slightly above the markers
			.attr("y2", -15)
			.attr("stroke", "#72738E47")
			.attr("stroke-width", 3);

		// Create agent markers and store reference
		agentMarkers = proctoringMarkersGroup
			.selectAll(".agent-marker")
			.data(agentProctoringTimes)
			.enter()
			.append("g")
			.attr("class", "agent-marker")
			.attr("transform", (d) => `translate(${x(d)},0)`)
			.each(function() {
				const g = d3.select(this);
				g.append("circle")
					.attr("r", 8)
					.attr("cy", -15)
					.attr("fill", "#72738E");
				g.append("text")
					.text("A")
					.attr("text-anchor", "middle")
					.attr("dy", "-10px")
					.attr("fill", "white")
					.style("font-weight", "primary");
			});

		// Create browser markers and store reference
		browserMarkers = proctoringMarkersGroup
			.selectAll(".browser-marker")
			.data(browserProctoringTimes)
			.enter()
			.append("g")
			.attr("class", "browser-marker")
			.attr("transform", (d) => `translate(${x(d)},0)`)
			.each(function() {
				const g = d3.select(this);
				g.append("circle")
					.attr("r", 8)
					.attr("cy", -15)
					.attr("fill", "#4B81AE");
				g.append("text")
					.text("B")
					.attr("text-anchor", "middle")
					.attr("dy", "-10px")
					.attr("fill", "white")
					.style("font-weight", "primary");
			});

		// Function to update window highlights
		function updateWindowHighlights() {
			windowHighlights
				.attr("x", (d) => x(d.start))
				.attr("width", (d) => x(d.end) - x(d.start));
		}

		// Function to update chart with new zoom level
		function updateChart(newZoomLevel) {
			zoomLevel = newZoomLevel;
			currentSvgWidth = baseWidth * zoomLevel;

			// Update x scale
			x.range([0, currentSvgWidth]);

			proctoringBaseLine.attr("x2", currentSvgWidth);

			// Update window highlights
			updateWindowHighlights();

			// Update marker positions
			updateMarkerPositions();
			// Update lines
			lines.attr("d", (d) => lineGenerator(d.values));

			// Update x-axis with scrolling offset
			updateXAxis();

			// Update grids
			updateGrids();

			// Update proctoring markers positions
			proctoringMarkersGroup
				.selectAll(".agent-marker")
				.attr("transform", (d) => `translate(${x(d)},0)`);
			proctoringMarkersGroup
				.selectAll(".browser-marker")
				.attr("transform", (d) => `translate(${x(d)},0)`);

			// Apply current scroll position
			updateScrollPosition();
		}

		function updateMarkerPositions() {
			if (agentMarkers) {
				agentMarkers.attr("transform", (d) => `translate(${x(d)},0)`);
			}
			if (browserMarkers) {
				browserMarkers.attr("transform", (d) => `translate(${x(d)},0)`);
			}
		}

		function updateXAxis() {
			// Calculate visible range based on scroll position
			const visibleStart = x.invert(scrollX);
			const visibleEnd = x.invert(scrollX + baseWidth);

			// Generate ticks for visible area
			const tickStart = Math.max(
				tickInterval,
				Math.ceil(visibleStart / tickInterval) * tickInterval
			);
			const tickEnd = Math.min(
				maxLength,
				Math.floor(visibleEnd / tickInterval) * tickInterval
			);
			const visibleTicks = d3.range(tickStart, tickEnd + 1, tickInterval);

			xAxisGroup.call(
				d3
					.axisBottom(x)
					.tickValues(visibleTicks)
					.tickFormat(
						(d) => `${Math.floor(d / 60)}:${String(d % 60).padStart(2, "0")}`
					)
			);

			// Adjust tick positions for scrolling
			xAxisGroup
				.selectAll(".tick")
				.attr("transform", (d) => `translate(${x(d) - scrollX},0)`);
		}

		function updateGrids() {
			// Y-grid lines
			yGridGroup.call(
				d3
					.axisLeft(y)
					.tickSize(-currentSvgWidth)
					.tickFormat("")
					.ticks(5)
			);

			// X-grid lines
			xGridGroup.call(
				d3
					.axisBottom(x)
					.tickSize(-height)
					.tickFormat("")
					.tickValues(d3.range(tickInterval, maxLength + 1, tickInterval))
			);
		}

		function updateScrollPosition() {
			contentGroup.attr("transform", `translate(${-scrollX},0)`);
			updateXAxis();
			// updateMarkerPositions(); // Add this line
		}

		// Initial chart setup
		updateChart(1);

		// Style grid lines and axis paths/lines
		svg
			.selectAll(".grid line")
			.style("stroke", "#ccc")
			.style("stroke-dasharray", "3,3");
		svg
			.selectAll(".x-grid line")
			.style("stroke", "#eee")
			.style("stroke-dasharray", "2,2");
		svg.selectAll(".axis path, .axis line").style("stroke", "none");
		svg.selectAll("path.domain").style("stroke", "none");
		svg.selectAll(".tick line:not(.grid line)").style("stroke", "none");

		// Tooltip element
		const tooltip = container
			.append("div")
			.attr("class", "custom-tooltip")
			.style("position", "absolute")
			.style("background", "rgba(0,0,0,0.9)")
			.style("padding", "10px")
			.style("border-radius", "12px")
			.style("color", "#fff")
			.style("font-family", "sans-serif")
			.style("pointer-events", "none")
			.style("font-size", "14px")
			.style("box-shadow", "0 0 10px rgba(0,0,0,0.5)")
			.style("display", "none")
			.style("z-index", 9999);

		// Vertical anchor line
		const anchorLine = chartGroup
			.append("line")
			.attr("class", "anchor-line")
			.attr("stroke", "#999")
			.attr("stroke-width", 2)
			.attr("stroke-dasharray", "4")
			.attr("y1", 0)
			.attr("y2", height)
			.style("opacity", 0);

		// Mouse interaction area
		const mouseArea = chartGroup
			.append("rect")
			.attr("width", baseWidth)
			.attr("height", height)
			.attr("fill", "none")
			.attr("pointer-events", "all");

		// Wheel event for horizontal scrolling and zooming
		svg.on("wheel", function(event) {
			event.preventDefault();
			event.stopPropagation();

			tooltip.style("display", "none");
			anchorLine.style("opacity", 0);

			if (event.ctrlKey || event.metaKey) {
				// Zoom functionality
				const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1;
				const newZoomLevel = Math.max(1, Math.min(10, zoomLevel * zoomFactor));

				if (newZoomLevel !== zoomLevel) {
					const [mouseX] = d3.pointer(event, chartGroup.node());
					const focusTime = x.invert(mouseX + scrollX); // what time is under cursor

					// Calculate relative position in zoomed scale BEFORE updating
					const focusRatio = (x(focusTime) - scrollX) / baseWidth;

					// Update chart (this updates `x.range`)
					updateChart(newZoomLevel);
					const newFocusX = x(focusTime);
					scrollX = newFocusX - focusRatio * baseWidth;

					const maxScroll = Math.max(0, currentSvgWidth - baseWidth);
					scrollX = Math.max(0, Math.min(maxScroll, scrollX));

					updateScrollPosition();
				}
			} else {
				const scrollSpeed = 50;
				const delta = event.deltaY || event.deltaX || 0;

				if (delta !== 0) {
					const newScrollX = scrollX + (delta * scrollSpeed) / 100;
					const maxScroll = Math.max(0, currentSvgWidth - baseWidth);

					scrollX = Math.max(0, Math.min(maxScroll, newScrollX));
					updateScrollPosition();
				}
			}
		});

		mouseArea.on("click", function(event) {
			const [mx] = d3.pointer(event, this);
			const xValue = x.invert(mx + scrollX);
			const second = Math.round(xValue);

			if (second < 1 || second > maxLength) return;

			if (onTimeClick && typeof onTimeClick === "function") {
				const minutes = Math.floor(second / 60);
				const seconds = second % 60;
				const timeStr = `${minutes}:${String(seconds).padStart(2, "0")}`;
				onTimeClick(timeStr);
			}
		});

		// Mousemove and mouseout for tooltip & vertical line
		mouseArea
			.on("mousemove", function(event) {
				const [mx] = d3.pointer(event, this);
				const xValue = x.invert(mx + scrollX);
				const second = Math.round(xValue);

				if (second < 1 || second > maxLength) {
					tooltip.style("display", "none");
					anchorLine.style("opacity", 0);
					return;
				}

				const tooltipData = validCategories.map((cat) => ({
					label: cat.key,
					color: color(cat.key),
					value: cat.data[second - 1] || 0,
				}));

				// Check if this second has proctoring events
				const agentEvent = agentProctoringTimes.includes(second);
				const browserEvent = browserProctoringTimes.includes(second);

				// Check if this second is within any window range
				// const windowEvent = windowRanges.find(range =>
				// 	second >= range.start && second <= range.end
				// );

				let html = `<div style="margin-bottom:6px;"><strong>Time: ${Math.floor(
					second / 60
				)}:${String(second % 60).padStart(2, "0")}</strong></div>`;

				tooltipData.forEach((d) => {
					html += `
						<div style="display:flex; align-items:center; gap:6px; margin:4px 0;">
							<div style="width:12px; height:12px; background:${
								d.color
							}; border-radius:50%;"></div>
							<div>${d.label}: <strong>${d.value.toFixed(2)}</strong></div>
						</div>`;
				});

				// Add proctoring event info if present
				if (agentEvent || browserEvent) {
					html += `<div style="margin-top:6px; padding-top:6px; border-top:1px solid #444;">`;
					if (agentEvent) {
						html += `<div style="display:flex; align-items:center; gap:6px;">
							<div style="width:12px; height:12px; background:#72738E; border-radius:50%;"></div>
							<div>Agent Proctoring Event</div>
						</div>`;
					}
					if (browserEvent) {
						html += `<div style="display:flex; align-items:center; gap:6px;">
						<div style="width:12px; height:12px; background:#2196F3; border-radius:50%;"></div>
						<div>Browser Proctoring Event</div>
					</div>`;
					}
					html += `</div>`;
				}

				// Add window event info if present
				// if (windowEvent) {
				// 	html += `<div style="margin-top:6px; padding-top:6px; border-top:1px solid #444;">`;
				// 	html += `<div style="display:flex; align-items:center; gap:6px;">
				// 		<div style="width:12px; height:12px; background:red; border-radius:50%; opacity:0.6;"></div>
				// 		<div>Window ${windowEvent.window} (${windowEvent.duration}s duration)</div>
				// 	</div>`;
				// 	html += `</div>`;
				// }

				// Add arrow element
				html += `<div class="tooltip-arrow"></div>`;
				tooltip.html(html).style("display", "block");

				// Get dimensions
				const containerRect = container.node().getBoundingClientRect();
				const tooltipRect = tooltip.node().getBoundingClientRect();

				let left = event.clientX - containerRect.left + 12;
				let top = event.clientY - containerRect.top - tooltipRect.height / 2;
				let arrowPosition = "right";

				// Check right boundary
				if (left + tooltipRect.width > containerRect.width) {
					left = event.clientX - containerRect.left - tooltipRect.width - 12;
					arrowPosition = "left";
				}

				// Check left boundary
				if (left < 0) {
					left = 10;
				}

				// Check top and bottom boundaries
				if (top < 0) {
					top = 10;
				} else if (top + tooltipRect.height > containerRect.height) {
					top = containerRect.height - tooltipRect.height - 10;
				}

				tooltip.style("left", `${left}px`).style("top", `${top}px`);

				// Style arrow
				tooltip
					.select(".tooltip-arrow")
					.style("position", "absolute")
					.style("top", "50%")
					.style("transform", "translateY(-50%)")
					.style("width", "0")
					.style("height", "0")
					.style("border-top", "8px solid transparent")
					.style("border-bottom", "8px solid transparent")
					.style(arrowPosition === "right" ? "left" : "right", "-8px")
					.style(`border-${arrowPosition}`, "8px solid rgba(0,0,0,0.9)")
					.style(
						arrowPosition === "right" ? "border-left" : "border-right",
						"none"
					);

				// Update vertical anchor line
				const xCoord = x(second) - scrollX;
				if (xCoord >= 0 && xCoord <= baseWidth) {
					anchorLine
						.attr("x1", xCoord)
						.attr("x2", xCoord)
						.attr("data-time", second)
						.style("opacity", 1);
				} else {
					anchorLine.style("opacity", 0);
				}
			})
			.on("mouseout", () => {
				tooltip.style("display", "none");
				anchorLine.style("opacity", 0);
			});

		if (time !== null && !isNaN(time)) {
			if (time >= 1 && time <= maxLength) {
				// Clear any existing timeouts
				clearTimeout(this.zoomTimeout);
				clearTimeout(this.scrollTimeout);
				clearTimeout(this.tooltipTimeout);

				// Smooth zoom logic
				this.zoomTimeout = setTimeout(() => {
					const targetZoom = Math.min(6, Math.max(1.5, 6));
					const centerX = baseWidth / 2;
					const targetTime = Math.round(time);

					// Update chart to zoom level
					zoomLevel = targetZoom;
					updateChart(zoomLevel);

					// After zoom, scroll to time smoothly
					this.scrollTimeout = setTimeout(() => {
						const newTargetX = x(targetTime);
						const maxScroll = Math.max(0, currentSvgWidth - baseWidth);
						const scrollTarget = Math.max(
							0,
							Math.min(maxScroll, newTargetX - centerX)
						);
						let extraScroll = 0;
						if (time > 120) {
							extraScroll = 50;
						}
						scrollX = scrollTarget + extraScroll;
						updateScrollPosition();

						// Show tooltip with proper positioning accounting for viewBox
						this.tooltipTimeout = setTimeout(() => {
							// Get SVG dimensions and viewBox parameters
							const svgNode = svg.node();
							const viewBox = svgNode.viewBox.baseVal;
							const svgWidth = svgNode.clientWidth;
							const svgHeight = svgNode.clientHeight;

							// Calculate scaling factors
							const scaleX = svgWidth / viewBox.width;
							const scaleY = svgHeight / viewBox.height;

							// Calculate x-coordinate in viewBox coordinates
							const xCoord = (x(targetTime) - scrollX) * scaleX;

							// Update anchor line
							if (xCoord >= 0 && xCoord <= baseWidth * scaleX) {
								anchorLine
									.attr("x1", x(targetTime) - scrollX)
									.attr("x2", x(targetTime) - scrollX)
									.style("opacity", 1);
							} else {
								anchorLine.style("opacity", 0);
							}

							// Prepare tooltip data
							const tooltipData = validCategories.map((cat) => ({
								label: cat.key,
								color: color(cat.key),
								value: cat.data[targetTime - 1] || 0,
							}));

							const agentEvent = agentProctoringTimes.includes(time);
							const browserEvent = browserProctoringTimes.includes(time);

							// Build tooltip HTML
							let html = `<div style="margin-bottom:6px;"><strong>Time: ${Math.floor(
								targetTime / 60
							)}:${String(targetTime % 60).padStart(2, "0")}</strong></div>`;

							tooltipData.forEach((d) => {
								html += `
                            <div style="display:flex; align-items:center; gap:6px; margin:4px 0;">
                                <div style="width:12px; height:12px; background:${
																	d.color
																}; border-radius:50%;"></div>
                                <div>${d.label}: <strong>${d.value.toFixed(
									2
								)}</strong></div>
                            </div>`;
							});

							if (agentEvent || browserEvent) {
								html += `<div style="margin-top:6px; padding-top:6px; border-top:1px solid #444;">`;
								if (agentEvent) {
									html += `<div style="display:flex; align-items:center; gap:6px;">
                                <div style="width:12px; height:12px; background:#72738E; border-radius:50%;"></div>
                                <div>Agent Proctoring Event</div>
                            </div>`;
								}
								if (browserEvent) {
									html += `<div style="display:flex; align-items:center; gap:6px;">
                                <div style="width:12px; height:12px; background:#2196F3; border-radius:50%;"></div>
                                <div>Browser Proctoring Event</div>
                            </div>`;
								}
								html += `</div>`;
							}

							// Add arrow element
							html += `<div class="tooltip-arrow"></div>`;
							tooltip.html(html).style("display", "block");

							// Calculate position accounting for viewBox
							const containerRect = container.node().getBoundingClientRect();
							const tooltipRect = tooltip.node().getBoundingClientRect();

							// Calculate the actual screen position of the point
							const pointX =
								containerRect.left +
								margin.left +
								(x(targetTime) - scrollX) * scaleX;
							const pointY =
								containerRect.top + margin.top + (height / 2) * scaleY;

							// Position tooltip to the right of the point by default
							let left = pointX + 12;
							let top = pointY - tooltipRect.height / 2;
							let arrowPosition = "right";

							// Check right boundary
							if (left + tooltipRect.width > containerRect.right) {
								left = pointX - tooltipRect.width - 12;
								arrowPosition = "left";
							}

							// Check left boundary
							if (left < containerRect.left) {
								left = containerRect.left + 10;
							}

							// Check top and bottom boundaries
							if (top < containerRect.top) {
								top = containerRect.top + 10;
							} else if (top + tooltipRect.height > containerRect.bottom) {
								top = containerRect.bottom - tooltipRect.height - 10;
							}

							// Convert to container-relative coordinates
							const containerLeft = left - containerRect.left;
							const containerTop = top - containerRect.top;

							tooltip
								.style("left", `${containerLeft}px`)
								.style("top", `${containerTop}px`);

							// Style arrow
							tooltip
								.select(".tooltip-arrow")
								.style("position", "absolute")
								.style("top", "50%")
								.style("transform", "translateY(-50%)")
								.style("width", "0")
								.style("height", "0")
								.style("border-top", "8px solid transparent")
								.style("border-bottom", "8px solid transparent")
								.style(arrowPosition === "right" ? "left" : "right", "-8px")
								.style(`border-${arrowPosition}`, "8px solid rgba(0,0,0,0.9)")
								.style(
									arrowPosition === "right" ? "border-left" : "border-right",
									"none"
								);
						}, 400);
					}, 300);
				}, 100);
			}
		}

		return {
			zoomIn: () => {
				const newZoomLevel = Math.min(10, zoomLevel * 1.5);
				if (newZoomLevel !== zoomLevel) {
					zoomLevel = newZoomLevel;
					updateChart(zoomLevel);
				}
			},
			zoomOut: () => {
				const newZoomLevel = Math.max(1, zoomLevel * 0.75);
				if (newZoomLevel !== zoomLevel) {
					zoomLevel = newZoomLevel;
					updateChart(zoomLevel);
					if (zoomLevel === 1) {
						scrollX = 0;
						updateScrollPosition();
					}
				}
			},
			reset: () => {
				zoomLevel = 1;
				updateChart(zoomLevel);
				scrollX = 0;
				updateScrollPosition();
			},
		};
	}
}
